import { GroupPerformerTypeFilter, GroupPerformerTypes, RoleFilter } from '@shared/types';
import { RolePerformerType, RoleType } from '@talon/web/lib/api';
import { Box, FormField, Select, Text } from 'grommet';
import { User } from 'grommet-icons';
import { useRef, useState } from 'react';
import { AddNewButton, Delete, TalonInput } from '../..';

interface IRolePerformerTabProps {
  setRolePerformers: (p: IRolePerformer[]) => void;
  rolePerformers: IRolePerformer[];
}

export interface IRolePerformer {
  name: string;
  roleType: RoleType;
  performerType: RolePerformerType;
  groupSize: number;
}

interface IGroupPerformers {
  label: string;
  value: number;
}

export const defaultRolePerformer: IRolePerformer = {
  name: 'New Role',
  roleType: RoleType.Actor,
  performerType: RolePerformerType.OnCameraPrincipal,
  groupSize: 1,
};

export const formatDefaultName = (performer: IRolePerformer, i?: number, isDirty?: boolean) => {
  let outputName = `${performer.roleType} - ${performer.performerType}`;
  if (performer.name) {
    outputName = performer.name;
  } else if (!performer.name && isDirty) {
    outputName = '';
  }
  if (performer.groupSize > 1) {
    const groupTag = i && i >= 1 ? `(${i} of ${performer.groupSize})` : `of ${performer.groupSize}`;
    outputName = `${outputName} ${groupTag}`;
  }
  return outputName;
};

export const RolePerformersTab = ({ rolePerformers, setRolePerformers }: IRolePerformerTabProps) => {
  /** State **/
  const [dirtyInputs, setDirtyInputs] = useState<Record<number, boolean>>({});

  /** Computed **/
  const roleKeys = Object.keys(RoleType) as Array<keyof typeof RoleType>;
  const performerKeys = (Object.keys(RolePerformerType) as Array<keyof typeof RolePerformerType>).filter(
    (key) => !GroupPerformerTypeFilter.includes(RolePerformerType[key])
  );

  // create an array of IGroupPerformers with values from 3 to "9+"
  const groupSize: IGroupPerformers[] = [];
  for (let i = 3; i <= 9; i++) {
    groupSize.push({ label: i.toString(), value: i });
  }
  groupSize.push({ label: '9+', value: 10 });

  /** Refs **/
  const rolesAdded = useRef(rolePerformers.length);

  /** Methods **/
  const newRole = () => {
    const role = {
      name: `New Role ${(rolesAdded.current += 1)}`,
      performerType: RolePerformerType.OnCameraPrincipal,
      roleType: RoleType.Actor,
      groupSize: 1,
    };
    setRolePerformers([...rolePerformers, role]);
  };

  /** Render **/
  return (
    <Box gap="medium" overflow={{ vertical: 'auto' }} height={{ max: 'large' }} fill>
      <Box flex="grow" pad="medium">
        <Text>Almost done! Let's finish up by discussing roles. Who's going to be in this commercial?</Text>
        <Box direction="row" gap="small" pad={{ top: 'small', right: 'large' }} justify="end">
          <AddNewButton secondary label="Add Role" tip="Add Role" icon={<User />} onClick={() => newRole()} />
        </Box>
        <Box gap="small" pad={{ left: 'small' }} margin={{ top: 'small' }}>
          {rolePerformers.map((performer, i) => (
            <Box
              key={i}
              border="all"
              round="small"
              pad="small"
              gap="small"
              margin={{ bottom: 'small' }}
              background="light-1"
            >
              <Box direction="row" gap="small" pad="xsmall">
                <TalonInput
                  id={`performer-${i}`}
                  name={`performer-${i}`}
                  label={i === 0 ? 'Role Name' : undefined}
                  htmlFor={`performer-${i}`}
                  placeholder="Performer Name"
                  val={formatDefaultName(performer, i, dirtyInputs[i]) || ''}
                  onChange={(val) => {
                    const newPerformers = [...rolePerformers];
                    newPerformers[i].name = val;
                    setRolePerformers(newPerformers);
                    setDirtyInputs((prev) => ({
                      ...prev,
                      [i]: true,
                    }));
                  }}
                  width="large"
                  textSize="small"
                />
              </Box>
              <Box direction="row" gap="small" pad="xsmall">
                <FormField
                  label={i === 0 ? <Text size="small">Performer Type</Text> : <></>}
                  htmlFor={`performer-type-${i}`}
                  width="medium"
                >
                  <Select
                    id={`performer-type-${i}`}
                    name={`performer-type-${i}`}
                    placeholder="Performer Type"
                    options={performerKeys.map((key) => RolePerformerType[key])}
                    value={performer.performerType}
                    onChange={({ option }) => {
                      const newPerformers = [...rolePerformers];
                      newPerformers[i].performerType = option;
                      !GroupPerformerTypes.includes(option) && (newPerformers[i].groupSize = 1);
                      setRolePerformers(newPerformers);
                    }}
                    size="small"
                  />
                </FormField>
                <FormField
                  label={i === 0 ? <Text size="small">Role Type</Text> : <></>}
                  htmlFor={`role-type-${i}`}
                  width="small"
                >
                  <Select
                    id={`role-type-${i}`}
                    name={`role-type-${i}`}
                    options={roleKeys
                      .filter((key) => RoleFilter[performer.performerType]?.includes(RoleType[key]))
                      .map((key) => RoleType[key])}
                    value={performer.roleType}
                    onChange={({ option }) => {
                      const newPerformers = [...rolePerformers];
                      newPerformers[i].roleType = option;
                      setRolePerformers(newPerformers);
                    }}
                    size="small"
                  />
                </FormField>
                {GroupPerformerTypes.includes(performer.performerType) && (
                  <FormField
                    label={i === 0 ? <Text size="small">Group Size</Text> : <></>}
                    htmlFor={`group-size-${i}`}
                    width="small"
                  >
                    <Select
                      id={`group-size-${i}`}
                      name={`group-size-${i}`}
                      options={groupSize}
                      value={performer.groupSize}
                      labelKey="label"
                      valueKey={{ key: 'value', reduce: true }}
                      onChange={({ option }) => {
                        const newPerformers = [...rolePerformers];
                        newPerformers[i].groupSize = option.value;
                        setRolePerformers(newPerformers);
                      }}
                      size="small"
                    />
                  </FormField>
                )}
                <Delete
                  onClick={() => {
                    const newPerformers = [...rolePerformers];
                    newPerformers.splice(i, 1);
                    setRolePerformers(newPerformers);
                    return new Promise((resolve) => resolve(true));
                  }}
                  name={performer.name || `${performer.roleType} - ${performer.performerType}`}
                  size="small"
                  tip="Delete Role"
                  hoverIndicator
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
